import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { updateAgent, type Agent } from './api';

type Recipient = { label: string; address: string };

type PaymentConfig = {
  recipients?: Record<string, string>;
  max_per_tx_native?: string;
  default_chain?: string;
  allowed_http_domains?: string[];
};

const CHAINS = ['ethereum', 'base', 'arbitrum', 'optimism', 'polygon'];

export function PaymentConfigForm({ orgId, agent }: { orgId: string; agent: Agent }) {
  const qc = useQueryClient();
  const cfg = agent.config as PaymentConfig;

  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [maxPerTxEth, setMaxPerTxEth] = useState('');
  const [defaultChain, setDefaultChain] = useState('ethereum');
  const [allowedDomainsInput, setAllowedDomainsInput] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRecipients(
      Object.entries(cfg.recipients ?? {}).map(([label, address]) => ({ label, address })),
    );
    setMaxPerTxEth(cfg.max_per_tx_native ? weiToEth(cfg.max_per_tx_native) : '');
    setDefaultChain(cfg.default_chain ?? 'ethereum');
    setAllowedDomainsInput((cfg.allowed_http_domains ?? []).join('\n'));
  }, [agent.id]);

  const save = useMutation({
    mutationFn: () => {
      const recipientsObj: Record<string, string> = {};
      for (const r of recipients) {
        if (r.label.trim() && r.address.trim()) {
          recipientsObj[r.label.trim()] = r.address.trim();
        }
      }
      const allowedDomains = allowedDomainsInput
        .split(/[\n,\s]+/)
        .map((d) => d.trim())
        .filter(Boolean);
      const config: PaymentConfig = {
        recipients: recipientsObj,
        max_per_tx_native: maxPerTxEth ? ethToWei(maxPerTxEth) : undefined,
        default_chain: defaultChain,
        allowed_http_domains: allowedDomains.length > 0 ? allowedDomains : undefined,
      };
      return updateAgent(orgId, agent.id, { config });
    },
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      qc.invalidateQueries({ queryKey: ['agent', orgId, agent.id] });
    },
    onError: (e: unknown) => setError(extractError(e)),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    save.mutate();
  }

  function addRow() {
    setRecipients((rs) => [...rs, { label: '', address: '' }]);
  }

  function removeRow(i: number) {
    setRecipients((rs) => rs.filter((_, idx) => idx !== i));
  }

  function updateRow(i: number, field: 'label' | 'address', v: string) {
    setRecipients((rs) => rs.map((r, idx) => (idx === i ? { ...r, [field]: v } : r)));
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader
          title="Allowed recipients"
          description="The only addresses this agent can pay. Anything else gets rejected at tool-call time, before the TEE is even contacted."
        />
        <CardBody className="space-y-2">
          {recipients.length === 0 && (
            <p className="text-sm text-ink-tertiary">
              No recipients yet. Add at least one or the agent won't be able to do anything.
            </p>
          )}
          {recipients.map((r, i) => (
            <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_2fr_auto]">
              <Input
                placeholder="Label (e.g. office-rent)"
                value={r.label}
                onChange={(e) => updateRow(i, 'label', e.target.value)}
              />
              <Input
                placeholder="0x..."
                value={r.address}
                onChange={(e) => updateRow(i, 'address', e.target.value)}
                className="font-mono text-xs"
              />
              <Button type="button" variant="ghost" onClick={() => removeRow(i)}>
                Remove
              </Button>
            </div>
          ))}
          <div>
            <Button type="button" variant="ghost" onClick={addRow}>
              + Add recipient
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Limits" description="Hard caps the LLM cannot exceed. Enforced by the tool before signing." />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Max per tx (native, in ETH/SOL/etc.)"
            placeholder="0.1"
            value={maxPerTxEth}
            onChange={(e) => setMaxPerTxEth(e.target.value)}
          />
          <div>
            <label className="block text-xs font-medium uppercase tracking-eyebrow text-ink-tertiary">
              Default chain
            </label>
            <select
              className="mt-1 block h-10 w-full rounded-lg border border-line bg-surface-white px-3 text-sm text-ink-primary focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
              value={defaultChain}
              onChange={(e) => setDefaultChain(e.target.value)}
            >
              {CHAINS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Web fetch allowlist"
          description="Domains the http_fetch tool can reach. Leave empty to allow any domain (not recommended in production)."
        />
        <CardBody>
          <textarea
            value={allowedDomainsInput}
            onChange={(e) => setAllowedDomainsInput(e.target.value)}
            placeholder={`api.stripe.com\napi.shopify.com\ncoingecko.com`}
            rows={4}
            className="block w-full rounded-lg border border-line bg-surface-white px-3 py-2 font-mono text-xs text-ink-primary focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
          />
          <p className="mt-1 text-xs text-ink-tertiary">
            One domain per line. Subdomains are matched too — adding{' '}
            <code className="rounded bg-surface-soft px-1">stripe.com</code> allows{' '}
            <code className="rounded bg-surface-soft px-1">api.stripe.com</code>.
          </p>
        </CardBody>
      </Card>

      {error && (
        <div className="rounded-md border border-danger/20 bg-danger/5 px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        {saved && <span className="text-sm text-ok">Saved.</span>}
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save configuration'}
        </Button>
      </div>
    </form>
  );
}

function ethToWei(s: string): string {
  const [whole, frac = ''] = s.split('.');
  const padded = (frac + '000000000000000000').slice(0, 18);
  const wei = BigInt(whole || '0') * 10n ** 18n + BigInt(padded || '0');
  return wei.toString();
}

function weiToEth(wei: string): string {
  try {
    const n = BigInt(wei);
    const whole = n / 10n ** 18n;
    const frac = (n % 10n ** 18n).toString().padStart(18, '0').replace(/0+$/, '');
    return frac ? `${whole}.${frac}` : whole.toString();
  } catch {
    return wei;
  }
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const resp = (e as { response?: { data?: { error?: { message?: string; code?: string } } } }).response;
    if (resp?.data?.error) return resp.data.error.message ?? resp.data.error.code ?? 'unknown error';
  }
  return e instanceof Error ? e.message : 'unknown error';
}
